/**
 * useLayoutConfig Hook
 *
 * Layout config persistence hook — loads, caches, auto-saves layout
 * preferences via LiveTradingSettingsService.
 *
 * Manages:
 * - Loading layout config from IndexedDB on enable
 * - Preset selection with immediate persistence
 * - Debounced pane weight auto-save (500ms)
 * - Debounced slot assignment auto-save (500ms)
 * - Banner visibility toggle with immediate persistence
 * - Banner position selection with immediate persistence
 * - Per-widget visibility toggle with immediate persistence
 * - Saved layout CRUD (create, duplicate, rename, delete, apply, save-to-active)
 * - Flush-on-unmount for pending saves
 * - effectivePresetId fallback for unsupported presets
 *
 * @see services/LiveTradingSettingsService.ts for persistence API
 * @see constants/layout.ts for preset definitions and phase-scoped constants
 * @see types/shared/Layout.ts for type definitions
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { BANNER_WIDGETS, BANNER_WIDGET_IDS } from '@/constants/bannerWidget';
import { PHASE_AVAILABLE_PRESETS, FALLBACK_PRESET_ID, DEFAULT_SLOT_ASSIGNMENTS } from '@/constants/layout';
import { liveTradingSettingsService } from '@/services/LiveTradingSettingsService';
import { generateUUID } from '@/utils/uuid';

import type {
  BannerAlignment,
  BannerPosition,
  BannerWidgetId,
  ClockWidgetConfig,
  FeatureAreaId,
  LayoutConfig,
  LayoutPresetId,
  SavedLayout,
  SlotId,
  SplitPointId,
  WidgetConfigs,
} from '@/types/shared';

// ===== CONSTANTS =====

/** Debounce delay for pane weight auto-save */
const WEIGHT_SAVE_DEBOUNCE_MS = 500;

/** Maps legacy preset IDs to their current replacements (Phase 5B + Phase 7) */
const LEGACY_PRESET_ID_MAP: Partial<Record<string, LayoutPresetId>> = {
  '5-pane-top-left-wide': '5-pane-rows-2-3',
  '5-pane-top-right-wide': '5-pane-rows-2-3',
  '5-pane-bottom-left-wide': '5-pane-rows-3-2',
  '5-pane-bottom-right-wide': '5-pane-rows-3-2',
  '5-pane-left-tall': '5-pane-rail-left',
  '5-pane-right-tall': '5-pane-rail-right',
  '6-pane-grid': '6-pane-3x2-rows-first',
};

// ===== INTERFACES =====

export interface UseLayoutConfigOptions {
  enabled: boolean;
}

/**
 * Banner config with runtime defaults applied.
 * Consumers use this instead of raw `bannerConfig` to avoid duplicating
 * default resolution logic (position, per-widget visibility).
 */
export interface ResolvedBannerConfig {
  visible: boolean;
  position: BannerPosition;
  widgets: Partial<Record<BannerWidgetId, boolean>>;
  widgetConfigs: WidgetConfigs;
  widgetOrder: BannerWidgetId[];
  widgetAlignment: BannerAlignment;
}

export interface UseLayoutConfigReturn {
  layoutConfig: LayoutConfig | null;
  effectivePresetId: LayoutPresetId;
  resolvedBannerConfig: ResolvedBannerConfig;
  isLoading: boolean;
  savedLayouts: SavedLayout[];
  activeLayoutId: string | undefined;
  /** Whether changes auto-save to the active named layout (default: true) */
  autosaveEnabled: boolean;
  /** Whether workingLayout has diverged from the active SavedLayout snapshot */
  isDirty: boolean;
  setActivePreset: (presetId: LayoutPresetId) => void;
  handleWeightChange: (splitPointId: SplitPointId, weight: number) => void;
  handleSlotAssignmentChange: (slotAssignments: Partial<Record<SlotId, FeatureAreaId>>) => void;
  toggleBannerVisibility: () => void;
  setBannerPosition: (position: BannerPosition) => void;
  toggleWidgetVisibility: (widgetId: BannerWidgetId) => void;
  updateClockConfig: (config: ClockWidgetConfig | undefined) => void;
  setWidgetOrder: (order: BannerWidgetId[]) => void;
  setWidgetAlignment: (alignment: BannerAlignment) => void;
  createSavedLayout: (name: string) => Promise<void>;
  duplicateSavedLayout: (newName: string) => Promise<void>;
  duplicateSavedLayoutById: (layoutId: string, newName: string) => Promise<void>;
  renameSavedLayout: (layoutId: string, newName: string) => Promise<void>;
  deleteSavedLayout: (layoutId: string) => Promise<void>;
  applySavedLayout: (layoutId: string) => Promise<void>;
  saveToActiveLayout: () => Promise<void>;
  /** Toggle autosave on/off. Toggle-ON while dirty saves snapshot atomically. */
  setAutosaveEnabled: (enabled: boolean) => Promise<void>;
  flushPendingWeights: () => Promise<void>;
}

// ===== HELPERS =====

/**
 * Cancel pending debounce timer and clear pending weights ref.
 * Does NOT persist the stale snapshot — callers persist a fresh
 * config that already includes the latest weights from layoutConfigRef.
 */
function cancelPendingDebounce(
  saveTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  pendingWeightsRef: React.MutableRefObject<LayoutConfig | null>,
): void {
  if (saveTimerRef.current) {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
  }
  pendingWeightsRef.current = null;
}

/**
 * If autosave is ON and an active named layout exists, augment the config
 * by snapshotting current workingLayout into the active SavedLayout entry.
 * Returns config unchanged if autosave is OFF, no activeLayoutId, or
 * activeLayoutId is stale (not found in savedLayouts).
 *
 * Pure function — no side effects. Callers use the returned config for both
 * state updates and persistence so state and DB stay in sync.
 */
function applyAutosaveSnapshot(config: LayoutConfig): LayoutConfig {
  const autosaveEnabled = config.autosaveEnabled ?? true;
  if (!autosaveEnabled || !config.activeLayoutId) return config;

  const layouts = config.savedLayouts ?? [];
  const activeLayout = layouts.find((l) => l.id === config.activeLayoutId);
  if (!activeLayout) return config;

  const wl = config.workingLayout;
  const now = new Date().toISOString();

  return {
    ...config,
    savedLayouts: layouts.map((l) =>
      l.id === config.activeLayoutId
        ? {
            id: l.id,
            name: l.name,
            createdAt: l.createdAt,
            presetId: wl.presetId,
            bannerConfig: structuredClone(wl.bannerConfig),
            ...(wl.paneWeights ? { paneWeights: { ...wl.paneWeights } } : {}),
            ...(wl.slotAssignments ? { slotAssignments: { ...wl.slotAssignments } } : {}),
            updatedAt: now,
          }
        : l,
    ),
  };
}

// ===== HOOK =====

export function useLayoutConfig(options: UseLayoutConfigOptions): UseLayoutConfigReturn {
  const { enabled } = options;

  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Mirror of layoutConfig state — enables synchronous reads in callbacks
  // without functional updaters. Inline-synced after every setLayoutConfig
  // call; useEffect safety net catches load-effect updates.
  const layoutConfigRef = useRef<LayoutConfig | null>(null);

  // Debounce refs for pane weight auto-save
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingWeightsRef = useRef<LayoutConfig | null>(null);

  // Track if hook has loaded to prevent re-loading on re-renders
  const hasLoadedRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Ref safety-net sync (catches load-effect state changes)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    layoutConfigRef.current = layoutConfig;
  }, [layoutConfig]);

  // ---------------------------------------------------------------------------
  // Derived: effectivePresetId
  // ---------------------------------------------------------------------------

  const effectivePresetId: LayoutPresetId =
    layoutConfig !== null &&
    (PHASE_AVAILABLE_PRESETS as readonly string[]).includes(layoutConfig.activePresetId)
      ? layoutConfig.activePresetId
      : FALLBACK_PRESET_ID;

  // ---------------------------------------------------------------------------
  // Derived: resolvedBannerConfig (centralized runtime defaults)
  // ---------------------------------------------------------------------------

  const rawBannerConfig = layoutConfig?.workingLayout.bannerConfig;

  const resolvedBannerConfig: ResolvedBannerConfig = useMemo(() => ({
    visible: rawBannerConfig?.visible ?? true,
    position: rawBannerConfig?.position ?? 'top',
    widgets: rawBannerConfig?.widgets ?? {},
    widgetConfigs: rawBannerConfig?.widgetConfigs ?? {},
    widgetOrder: rawBannerConfig?.widgetOrder ?? [...BANNER_WIDGET_IDS],
    widgetAlignment: rawBannerConfig?.widgetAlignment ?? 'center',
  }), [rawBannerConfig?.visible, rawBannerConfig?.position, rawBannerConfig?.widgets, rawBannerConfig?.widgetConfigs, rawBannerConfig?.widgetOrder, rawBannerConfig?.widgetAlignment]);

  // ---------------------------------------------------------------------------
  // Derived: savedLayouts and activeLayoutId (T12)
  // ---------------------------------------------------------------------------

  const savedLayouts: SavedLayout[] = layoutConfig?.savedLayouts ?? [];
  const activeLayoutId: string | undefined = layoutConfig?.activeLayoutId;

  // ---------------------------------------------------------------------------
  // Derived: autosaveEnabled (T12C) — defensive fallback for unmigrated records
  // ---------------------------------------------------------------------------

  const autosaveEnabled: boolean = layoutConfig?.autosaveEnabled ?? true;

  // ---------------------------------------------------------------------------
  // Derived: isDirty (T12C) — workingLayout vs active SavedLayout snapshot
  // ---------------------------------------------------------------------------

  const isDirty: boolean = useMemo(() => {
    if (!layoutConfig?.activeLayoutId) return false;

    const layouts = layoutConfig.savedLayouts ?? [];
    const activeLayout = layouts.find((l) => l.id === layoutConfig.activeLayoutId);
    if (!activeLayout) return false;

    const wl = layoutConfig.workingLayout;

    // Compare meaningful fields — excludes updatedAt, identity fields
    if (wl.presetId !== activeLayout.presetId) return true;
    if (JSON.stringify(wl.bannerConfig) !== JSON.stringify(activeLayout.bannerConfig)) return true;
    if (JSON.stringify(wl.paneWeights) !== JSON.stringify(activeLayout.paneWeights)) return true;
    if (JSON.stringify(wl.slotAssignments) !== JSON.stringify(activeLayout.slotAssignments)) return true;

    return false;
  }, [layoutConfig]);

  // ---------------------------------------------------------------------------
  // Persistence helper — throws on failure so flushPendingWeights can
  // propagate errors to TradingHub for transition blocking.
  // Fire-and-forget callers use .catch() to handle errors locally.
  // ---------------------------------------------------------------------------

  const persistConfig = useCallback(async (config: LayoutConfig): Promise<void> => {
    await liveTradingSettingsService.updateLayoutConfig(config);
  }, []);

  // ---------------------------------------------------------------------------
  // Load effect
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!enabled) {
      hasLoadedRef.current = false;
      return;
    }

    if (hasLoadedRef.current) return;

    let cancelled = false;

    const load = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const settings = await liveTradingSettingsService.getOrCreateDefaultSettings();
        if (cancelled) return;

        let config = settings.layoutConfig as LayoutConfig;

        // Migrate legacy 5-pane preset IDs (Phase 5B)
        let migrated = false;
        const activeMapping = LEGACY_PRESET_ID_MAP[config.activePresetId];
        const workingMapping = LEGACY_PRESET_ID_MAP[config.workingLayout.presetId];

        if (activeMapping || workingMapping) {
          config = {
            ...config,
            activePresetId: activeMapping ?? config.activePresetId,
            workingLayout: {
              ...config.workingLayout,
              presetId: workingMapping ?? config.workingLayout.presetId,
              updatedAt: new Date().toISOString(),
            },
          };
          // Discard old pane weights — split point IDs differ between old and new presets
          const { paneWeights: _discarded, ...restWorkingLayout } = config.workingLayout;
          config = { ...config, workingLayout: restWorkingLayout };
          migrated = true;
        }

        if (migrated) {
          await liveTradingSettingsService.updateLayoutConfig(config);
          if (cancelled) return;
        }

        // Coerce unsupported preset to fallback
        if (!(PHASE_AVAILABLE_PRESETS as readonly string[]).includes(config.activePresetId)) {
          config = {
            ...config,
            activePresetId: FALLBACK_PRESET_ID,
            workingLayout: {
              ...config.workingLayout,
              presetId: FALLBACK_PRESET_ID,
              updatedAt: new Date().toISOString(),
            },
          };
          // Remove paneWeights — new preset uses defaults
          // (exactOptionalPropertyTypes: use destructure + omit pattern)
          const { paneWeights: _discarded, ...restWorkingLayout } = config.workingLayout;
          config = {
            ...config,
            workingLayout: restWorkingLayout,
          };

          // Persist the coerced config
          await liveTradingSettingsService.updateLayoutConfig(config);
          if (cancelled) return;
        }

        // Invariant guard: ensure slotAssignments is present (defensive fallback
        // in case service migration didn't run or data is stale)
        if (!config.workingLayout.slotAssignments) {
          config = {
            ...config,
            workingLayout: {
              ...config.workingLayout,
              slotAssignments: { ...DEFAULT_SLOT_ASSIGNMENTS },
              updatedAt: new Date().toISOString(),
            },
          };
          await liveTradingSettingsService.updateLayoutConfig(config);
          if (cancelled) return;
        }

        // Bootstrap "Default" saved layout when savedLayouts is empty and no
        // active layout is set. Idempotent: guard prevents duplicate creation;
        // hasLoadedRef prevents re-execution on re-renders.
        if ((config.savedLayouts ?? []).length === 0 && !config.activeLayoutId) {
          const now = new Date().toISOString();
          const wl = config.workingLayout;
          const defaultLayout: SavedLayout = {
            id: generateUUID(),
            name: 'Default',
            presetId: wl.presetId,
            bannerConfig: structuredClone(wl.bannerConfig),
            ...(wl.paneWeights ? { paneWeights: { ...wl.paneWeights } } : {}),
            ...(wl.slotAssignments ? { slotAssignments: { ...wl.slotAssignments } } : {}),
            createdAt: now,
            updatedAt: now,
          };
          config = {
            ...config,
            savedLayouts: [defaultLayout],
            activeLayoutId: defaultLayout.id,
          };
          await liveTradingSettingsService.updateLayoutConfig(config);
          if (cancelled) return;
        }

        setLayoutConfig(config);
        layoutConfigRef.current = config;
        hasLoadedRef.current = true;
      } catch (error) {
        console.error('useLayoutConfig: Failed to load layout config:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // ---------------------------------------------------------------------------
  // setActivePreset
  // ---------------------------------------------------------------------------

  const setActivePreset = useCallback((presetId: LayoutPresetId): void => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    // Cancel pending debounce — old preset weights are discarded on switch
    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    const updated = applyAutosaveSnapshot({
      ...prev,
      activePresetId: presetId,
      workingLayout: {
        presetId,
        bannerConfig: prev.workingLayout.bannerConfig,
        // Slot assignments persist across preset changes (slots map by numeric ID)
        ...(prev.workingLayout.slotAssignments
          ? { slotAssignments: prev.workingLayout.slotAssignments }
          : {}),
        updatedAt: new Date().toISOString(),
      },
    });

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    // Persist immediately (deliberate user action, fire-and-forget)
    void persistConfig(updated).catch((error) => {
      console.error('useLayoutConfig: Preset change persist failed:', error);
      // Re-queue so flushPendingWeights can retry on transition (only if still current)
      if (pendingWeightsRef.current === null && layoutConfigRef.current === updated) {
        pendingWeightsRef.current = updated;
      }
    });
  }, [persistConfig]);

  // ---------------------------------------------------------------------------
  // handleWeightChange
  // ---------------------------------------------------------------------------

  const handleWeightChange = useCallback((splitPointId: SplitPointId, weight: number): void => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    const updated = applyAutosaveSnapshot({
      ...prev,
      workingLayout: {
        ...prev.workingLayout,
        paneWeights: {
          ...prev.workingLayout.paneWeights,
          [splitPointId]: weight,
        },
        updatedAt: new Date().toISOString(),
      },
    });

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    // Store in pending ref for debounced save
    pendingWeightsRef.current = updated;

    // Restart debounce timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      const pending = pendingWeightsRef.current;
      if (pending) {
        const captured = pending;
        void persistConfig(captured).then(() => {
          // Only clear ref if it still matches (prevents clearing newer values)
          if (pendingWeightsRef.current === captured) {
            pendingWeightsRef.current = null;
          }
        }).catch((error) => {
          console.error('useLayoutConfig: Debounced weight persist failed:', error);
          // Re-queue so flushPendingWeights can retry on transition (only if still current)
          if (pendingWeightsRef.current === null && layoutConfigRef.current === captured) {
            pendingWeightsRef.current = captured;
          }
        });
      }
      saveTimerRef.current = null;
    }, WEIGHT_SAVE_DEBOUNCE_MS);
  }, [persistConfig]);

  // ---------------------------------------------------------------------------
  // handleSlotAssignmentChange
  // ---------------------------------------------------------------------------

  const handleSlotAssignmentChange = useCallback(
    (slotAssignments: Partial<Record<SlotId, FeatureAreaId>>): void => {
      const prev = layoutConfigRef.current;
      if (!prev) return;

      const updated = applyAutosaveSnapshot({
        ...prev,
        workingLayout: {
          ...prev.workingLayout,
          slotAssignments,
          updatedAt: new Date().toISOString(),
        },
      });

      setLayoutConfig(updated);
      layoutConfigRef.current = updated;

      // Store in pending ref for debounced save
      pendingWeightsRef.current = updated;

      // Restart debounce timer
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = setTimeout(() => {
        const pending = pendingWeightsRef.current;
        if (pending) {
          const captured = pending;
          void persistConfig(captured).then(() => {
            // Only clear ref if it still matches (prevents clearing newer values)
            if (pendingWeightsRef.current === captured) {
              pendingWeightsRef.current = null;
            }
          }).catch((error) => {
            console.error('useLayoutConfig: Debounced slot assignment persist failed:', error);
            // Re-queue so flushPendingWeights can retry on transition (only if still current)
            if (pendingWeightsRef.current === null && layoutConfigRef.current === captured) {
              pendingWeightsRef.current = captured;
            }
          });
        }
        saveTimerRef.current = null;
      }, WEIGHT_SAVE_DEBOUNCE_MS);
    },
    [persistConfig],
  );

  // ---------------------------------------------------------------------------
  // toggleBannerVisibility
  // ---------------------------------------------------------------------------

  const toggleBannerVisibility = useCallback((): void => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    // Cancel pending debounce — new persist includes latest weights from ref
    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    const updated = applyAutosaveSnapshot({
      ...prev,
      workingLayout: {
        ...prev.workingLayout,
        bannerConfig: {
          ...prev.workingLayout.bannerConfig,
          visible: !prev.workingLayout.bannerConfig.visible,
        },
        updatedAt: new Date().toISOString(),
      },
    });

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    // Persist immediately (deliberate user action, fire-and-forget)
    void persistConfig(updated).catch((error) => {
      console.error('useLayoutConfig: Banner toggle persist failed:', error);
      // Re-queue so flushPendingWeights can retry on transition (only if still current)
      if (pendingWeightsRef.current === null && layoutConfigRef.current === updated) {
        pendingWeightsRef.current = updated;
      }
    });
  }, [persistConfig]);

  // ---------------------------------------------------------------------------
  // setBannerPosition
  // ---------------------------------------------------------------------------

  const setBannerPosition = useCallback((position: BannerPosition): void => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    // Cancel pending debounce — new persist includes latest weights from ref
    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    const updated = applyAutosaveSnapshot({
      ...prev,
      workingLayout: {
        ...prev.workingLayout,
        bannerConfig: {
          ...prev.workingLayout.bannerConfig,
          position,
        },
        updatedAt: new Date().toISOString(),
      },
    });

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    // Persist immediately (deliberate user action, fire-and-forget)
    void persistConfig(updated).catch((error) => {
      console.error('useLayoutConfig: Banner position persist failed:', error);
      if (pendingWeightsRef.current === null && layoutConfigRef.current === updated) {
        pendingWeightsRef.current = updated;
      }
    });
  }, [persistConfig]);

  // ---------------------------------------------------------------------------
  // toggleWidgetVisibility
  // ---------------------------------------------------------------------------

  const toggleWidgetVisibility = useCallback((widgetId: BannerWidgetId): void => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    // Cancel pending debounce — new persist includes latest weights from ref
    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    const currentWidgets = prev.workingLayout.bannerConfig.widgets ?? {};
    // Look up the widget's default from the registry
    const widgetConfig = BANNER_WIDGETS.find((w) => w.id === widgetId);
    const defaultEnabled = widgetConfig?.defaultEnabled ?? true;
    const currentVisibility = currentWidgets[widgetId] ?? defaultEnabled;

    const updated = applyAutosaveSnapshot({
      ...prev,
      workingLayout: {
        ...prev.workingLayout,
        bannerConfig: {
          ...prev.workingLayout.bannerConfig,
          widgets: {
            ...currentWidgets,
            [widgetId]: !currentVisibility,
          },
        },
        updatedAt: new Date().toISOString(),
      },
    });

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    // Persist immediately (deliberate user action, fire-and-forget)
    void persistConfig(updated).catch((error) => {
      console.error('useLayoutConfig: Widget toggle persist failed:', error);
      if (pendingWeightsRef.current === null && layoutConfigRef.current === updated) {
        pendingWeightsRef.current = updated;
      }
    });
  }, [persistConfig]);

  // ---------------------------------------------------------------------------
  // updateClockConfig
  // ---------------------------------------------------------------------------

  const updateClockConfig = useCallback((config: ClockWidgetConfig | undefined): void => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    // Cancel pending debounce — new persist includes latest weights from ref
    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    const existingWidgetConfigs = prev.workingLayout.bannerConfig.widgetConfigs;

    // When config is undefined, remove the clock key ("revert to default").
    // When defined, set/replace the clock config.
    // Use destructure + rest to comply with exactOptionalPropertyTypes.
    let nextWidgetConfigs: typeof existingWidgetConfigs;
    if (config === undefined) {
      if (existingWidgetConfigs) {
        const { clock: _removed, ...rest } = existingWidgetConfigs;
        nextWidgetConfigs = Object.keys(rest).length > 0 ? rest : undefined;
      } else {
        nextWidgetConfigs = undefined;
      }
    } else {
      nextWidgetConfigs = { ...existingWidgetConfigs, clock: config };
    }

    // Build bannerConfig: when clearing, destructure to remove widgetConfigs key
    // (spread of prev.workingLayout.bannerConfig would preserve the old key).
    let nextBannerConfig: typeof prev.workingLayout.bannerConfig;
    if (nextWidgetConfigs !== undefined) {
      nextBannerConfig = { ...prev.workingLayout.bannerConfig, widgetConfigs: nextWidgetConfigs };
    } else {
      const { widgetConfigs: _cleared, ...restBannerConfig } = prev.workingLayout.bannerConfig;
      nextBannerConfig = restBannerConfig;
    }

    const updated = applyAutosaveSnapshot({
      ...prev,
      workingLayout: {
        ...prev.workingLayout,
        bannerConfig: nextBannerConfig,
        updatedAt: new Date().toISOString(),
      },
    });

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    // Persist immediately (deliberate user action, fire-and-forget)
    void persistConfig(updated).catch((error) => {
      console.error('useLayoutConfig: Clock config persist failed:', error);
      if (pendingWeightsRef.current === null && layoutConfigRef.current === updated) {
        pendingWeightsRef.current = updated;
      }
    });
  }, [persistConfig]);

  // ---------------------------------------------------------------------------
  // setWidgetOrder
  // ---------------------------------------------------------------------------

  const setWidgetOrder = useCallback((order: BannerWidgetId[]): void => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    // Cancel pending debounce — new persist includes latest weights from ref
    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    // Normalize: filter to known IDs and deduplicate, preserving caller order
    const knownIds = new Set<BannerWidgetId>(BANNER_WIDGET_IDS);
    const seen = new Set<BannerWidgetId>();
    const normalized = order.filter((id) => {
      if (!knownIds.has(id) || seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    const updated = applyAutosaveSnapshot({
      ...prev,
      workingLayout: {
        ...prev.workingLayout,
        bannerConfig: {
          ...prev.workingLayout.bannerConfig,
          widgetOrder: normalized,
        },
        updatedAt: new Date().toISOString(),
      },
    });

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    // Persist immediately (deliberate user action, fire-and-forget)
    void persistConfig(updated).catch((error) => {
      console.error('useLayoutConfig: Widget order persist failed:', error);
      if (pendingWeightsRef.current === null && layoutConfigRef.current === updated) {
        pendingWeightsRef.current = updated;
      }
    });
  }, [persistConfig]);

  // ---------------------------------------------------------------------------
  // setWidgetAlignment
  // ---------------------------------------------------------------------------

  const setWidgetAlignment = useCallback((alignment: BannerAlignment): void => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    // Cancel pending debounce — new persist includes latest weights from ref
    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    const updated = applyAutosaveSnapshot({
      ...prev,
      workingLayout: {
        ...prev.workingLayout,
        bannerConfig: {
          ...prev.workingLayout.bannerConfig,
          widgetAlignment: alignment,
        },
        updatedAt: new Date().toISOString(),
      },
    });

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    // Persist immediately (deliberate user action, fire-and-forget)
    void persistConfig(updated).catch((error) => {
      console.error('useLayoutConfig: Widget alignment persist failed:', error);
      if (pendingWeightsRef.current === null && layoutConfigRef.current === updated) {
        pendingWeightsRef.current = updated;
      }
    });
  }, [persistConfig]);

  // ---------------------------------------------------------------------------
  // Saved Layout CRUD (T12) — optimistic state + rollback on persist failure
  // ---------------------------------------------------------------------------

  const createSavedLayout = useCallback(async (name: string): Promise<void> => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      throw new Error('Layout name cannot be empty');
    }

    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    const now = new Date().toISOString();
    const workingLayout = prev.workingLayout;

    // Snapshot current workingLayout into a new SavedLayout.
    // Deep-clone bannerConfig (nested objects); shallow-clone flat records.
    const newLayout: SavedLayout = {
      id: generateUUID(),
      name: trimmedName,
      presetId: workingLayout.presetId,
      bannerConfig: structuredClone(workingLayout.bannerConfig),
      ...(workingLayout.paneWeights ? { paneWeights: { ...workingLayout.paneWeights } } : {}),
      ...(workingLayout.slotAssignments ? { slotAssignments: { ...workingLayout.slotAssignments } } : {}),
      createdAt: now,
      updatedAt: now,
    };

    const updated: LayoutConfig = {
      ...prev,
      savedLayouts: [...(prev.savedLayouts ?? []), newLayout],
      activeLayoutId: newLayout.id,
    };

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    try {
      await persistConfig(updated);
    } catch (error) {
      setLayoutConfig(prev);
      layoutConfigRef.current = prev;
      pendingWeightsRef.current = prev;
      throw error;
    }
  }, [persistConfig]);

  const duplicateSavedLayout = useCallback(async (newName: string): Promise<void> => {
    await createSavedLayout(newName);
  }, [createSavedLayout]);

  const duplicateSavedLayoutById = useCallback(async (layoutId: string, newName: string): Promise<void> => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    const trimmedName = newName.trim();
    if (!trimmedName) {
      throw new Error('Layout name cannot be empty');
    }

    const layouts = prev.savedLayouts ?? [];
    const targetLayout = layouts.find((l) => l.id === layoutId);
    if (!targetLayout) {
      throw new Error(`Saved layout not found: ${layoutId}`);
    }

    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    // Clone snapshot fields from the target saved layout (not working state).
    // Same cloning strategy: structuredClone for bannerConfig, conditional spread for flat records.
    const now = new Date().toISOString();
    const newLayout: SavedLayout = {
      id: generateUUID(),
      name: trimmedName,
      presetId: targetLayout.presetId,
      bannerConfig: structuredClone(targetLayout.bannerConfig),
      ...(targetLayout.paneWeights ? { paneWeights: { ...targetLayout.paneWeights } } : {}),
      ...(targetLayout.slotAssignments ? { slotAssignments: { ...targetLayout.slotAssignments } } : {}),
      createdAt: now,
      updatedAt: now,
    };

    const updated: LayoutConfig = {
      ...prev,
      savedLayouts: [...layouts, newLayout],
      activeLayoutId: newLayout.id,
    };

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    try {
      await persistConfig(updated);
    } catch (error) {
      setLayoutConfig(prev);
      layoutConfigRef.current = prev;
      pendingWeightsRef.current = prev;
      throw error;
    }
  }, [persistConfig]);

  const renameSavedLayout = useCallback(async (layoutId: string, newName: string): Promise<void> => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    const trimmedName = newName.trim();
    if (!trimmedName) {
      throw new Error('Layout name cannot be empty');
    }

    const layouts = prev.savedLayouts ?? [];
    const targetLayout = layouts.find((l) => l.id === layoutId);
    if (!targetLayout) {
      throw new Error(`Saved layout not found: ${layoutId}`);
    }

    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    const updated: LayoutConfig = {
      ...prev,
      savedLayouts: layouts.map((l) =>
        l.id === layoutId
          ? { ...l, name: trimmedName, updatedAt: new Date().toISOString() }
          : l,
      ),
    };

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    try {
      await persistConfig(updated);
    } catch (error) {
      setLayoutConfig(prev);
      layoutConfigRef.current = prev;
      pendingWeightsRef.current = prev;
      throw error;
    }
  }, [persistConfig]);

  const deleteSavedLayout = useCallback(async (layoutId: string): Promise<void> => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    const layouts = prev.savedLayouts ?? [];
    const targetLayout = layouts.find((l) => l.id === layoutId);
    if (!targetLayout) {
      throw new Error(`Saved layout not found: ${layoutId}`);
    }
    if (layouts.length <= 1) {
      throw new Error('Cannot delete the last saved layout');
    }

    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    const filteredLayouts = layouts.filter((l) => l.id !== layoutId);

    // If deleting the active layout, clear activeLayoutId (exactOptionalPropertyTypes)
    let updated: LayoutConfig;
    if (prev.activeLayoutId === layoutId) {
      const { activeLayoutId: _, ...configWithoutActiveId } = prev;
      updated = {
        ...configWithoutActiveId,
        savedLayouts: filteredLayouts,
      };
    } else {
      updated = {
        ...prev,
        savedLayouts: filteredLayouts,
      };
    }

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    try {
      await persistConfig(updated);
    } catch (error) {
      setLayoutConfig(prev);
      layoutConfigRef.current = prev;
      pendingWeightsRef.current = prev;
      throw error;
    }
  }, [persistConfig]);

  const applySavedLayout = useCallback(async (layoutId: string): Promise<void> => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    const layouts = prev.savedLayouts ?? [];
    const targetLayout = layouts.find((l) => l.id === layoutId);
    if (!targetLayout) {
      throw new Error(`Saved layout not found: ${layoutId}`);
    }

    // Cancel pending debounce — safe because applyAutosaveSnapshot already
    // projected any pending changes into savedLayouts at state-update time.
    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    // Clone snapshot fields to avoid shared references between workingLayout and savedLayouts
    const newWorkingLayout = {
      presetId: targetLayout.presetId,
      bannerConfig: structuredClone(targetLayout.bannerConfig),
      ...(targetLayout.paneWeights ? { paneWeights: { ...targetLayout.paneWeights } } : {}),
      ...(targetLayout.slotAssignments ? { slotAssignments: { ...targetLayout.slotAssignments } } : {}),
      updatedAt: new Date().toISOString(),
    };

    const updated: LayoutConfig = {
      ...prev,
      activePresetId: targetLayout.presetId,
      workingLayout: newWorkingLayout,
      activeLayoutId: layoutId,
    };

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    try {
      await persistConfig(updated);
    } catch (error) {
      setLayoutConfig(prev);
      layoutConfigRef.current = prev;
      pendingWeightsRef.current = prev;
      throw error;
    }
  }, [persistConfig]);

  const saveToActiveLayout = useCallback(async (): Promise<void> => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    // No active layout — no-op
    if (!prev.activeLayoutId) return;

    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    const layouts = prev.savedLayouts ?? [];
    const activeLayout = layouts.find((l) => l.id === prev.activeLayoutId);

    // Stale reference — clear activeLayoutId silently (optimistic + persist)
    if (!activeLayout) {
      const { activeLayoutId: _, ...configWithoutActiveId } = prev;
      const cleaned: LayoutConfig = { ...configWithoutActiveId };

      setLayoutConfig(cleaned);
      layoutConfigRef.current = cleaned;

      try {
        await persistConfig(cleaned);
      } catch (_error) {
        setLayoutConfig(prev);
        layoutConfigRef.current = prev;
        pendingWeightsRef.current = prev;
      }
      return;
    }

    // Overwrite the active layout's snapshot with current working layout.
    // Rebuild from identity fields + fresh snapshot (avoids stale optional fields from spread).
    const now = new Date().toISOString();
    const workingLayout = prev.workingLayout;

    const updated: LayoutConfig = {
      ...prev,
      savedLayouts: layouts.map((l) =>
        l.id === prev.activeLayoutId
          ? {
              id: l.id,
              name: l.name,
              createdAt: l.createdAt,
              presetId: workingLayout.presetId,
              bannerConfig: structuredClone(workingLayout.bannerConfig),
              ...(workingLayout.paneWeights ? { paneWeights: { ...workingLayout.paneWeights } } : {}),
              ...(workingLayout.slotAssignments ? { slotAssignments: { ...workingLayout.slotAssignments } } : {}),
              updatedAt: now,
            }
          : l,
      ),
    };

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    try {
      await persistConfig(updated);
    } catch (error) {
      setLayoutConfig(prev);
      layoutConfigRef.current = prev;
      pendingWeightsRef.current = prev;
      throw error;
    }
  }, [persistConfig]);

  // ---------------------------------------------------------------------------
  // setAutosaveEnabled (T12C) — toggle autosave on/off with snapshot semantics
  // ---------------------------------------------------------------------------

  const setAutosaveEnabled = useCallback(async (enabled: boolean): Promise<void> => {
    const prev = layoutConfigRef.current;
    if (!prev) return;

    // No-op if already at the requested value
    if ((prev.autosaveEnabled ?? true) === enabled) return;

    cancelPendingDebounce(saveTimerRef, pendingWeightsRef);

    let updated: LayoutConfig;

    if (enabled) {
      // Toggle ON: set flag, then snapshot workingLayout into active SavedLayout.
      // applyAutosaveSnapshot handles the snapshot if an active layout exists.
      const toggled: LayoutConfig = { ...prev, autosaveEnabled: true };

      // Defensive: if activeLayoutId is stale (not found in savedLayouts), clear it
      const layouts = toggled.savedLayouts ?? [];
      if (toggled.activeLayoutId && !layouts.find((l) => l.id === toggled.activeLayoutId)) {
        const { activeLayoutId: _, ...configWithoutActiveId } = toggled;
        updated = applyAutosaveSnapshot({ ...configWithoutActiveId, autosaveEnabled: true });
      } else {
        updated = applyAutosaveSnapshot(toggled);
      }
    } else {
      // Toggle OFF: just change the flag, no snapshot needed
      updated = { ...prev, autosaveEnabled: false };
    }

    setLayoutConfig(updated);
    layoutConfigRef.current = updated;

    try {
      await persistConfig(updated);
    } catch (error) {
      // Rollback: revert to previous state (autosave stays at its old value)
      setLayoutConfig(prev);
      layoutConfigRef.current = prev;
      pendingWeightsRef.current = prev;
      throw error;
    }
  }, [persistConfig]);

  // ---------------------------------------------------------------------------
  // flushPendingWeights — errors propagate to caller (TradingHub transition)
  // ---------------------------------------------------------------------------

  const flushPendingWeights = useCallback(async (): Promise<void> => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    const pending = pendingWeightsRef.current;
    if (pending) {
      pendingWeightsRef.current = null;
      try {
        await persistConfig(pending);
      } catch (error) {
        // Restore so a subsequent flush can retry (only if still current)
        if (pendingWeightsRef.current === null && layoutConfigRef.current === pending) {
          pendingWeightsRef.current = pending;
        }
        throw error;
      }
    }
  }, [persistConfig]);

  // ---------------------------------------------------------------------------
  // Effect B: Unmount flush (empty deps)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      const pending = pendingWeightsRef.current;
      if (pending) {
        pendingWeightsRef.current = null;
        // Fire-and-forget on unmount
        void liveTradingSettingsService.updateLayoutConfig(pending).catch((error) => {
          console.error('useLayoutConfig: Unmount flush failed:', error);
        });
      }
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    layoutConfig,
    effectivePresetId,
    resolvedBannerConfig,
    isLoading,
    savedLayouts,
    activeLayoutId,
    autosaveEnabled,
    isDirty,
    setActivePreset,
    handleWeightChange,
    handleSlotAssignmentChange,
    toggleBannerVisibility,
    setBannerPosition,
    toggleWidgetVisibility,
    updateClockConfig,
    setWidgetOrder,
    setWidgetAlignment,
    createSavedLayout,
    duplicateSavedLayout,
    duplicateSavedLayoutById,
    renameSavedLayout,
    deleteSavedLayout,
    applySavedLayout,
    saveToActiveLayout,
    setAutosaveEnabled,
    flushPendingWeights,
  };
}
